import { Link } from "react-router-dom";
import { Facebook, Instagram, Mail, MapPin, Phone, Twitter } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSettings } from "@/lib/hooks";

const socialLinks = [
  { icon: Facebook, href: "https://www.facebook.com", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com", label: "Instagram" },
  { icon: Twitter, href: "https://x.com", label: "X" },
];

const legalLinks = [
  { to: "/hakkimizda", label: "Hakkımızda" },
  { to: "/kullanim-politikasi", label: "Kullanım Politikası" },
  { to: "/gizlilik-politikasi", label: "Gizlilik Politikası" },
  { to: "/kvkk", label: "KVKK Aydınlatma Metni" },
  { to: "/guvenli-internet", label: "Güvenli İnternet" },
  { to: "/yonetim-sistemleri-politikasi", label: "Yönetim Sistemleri Politikası" },
];

export default function Footer() {
  const { t } = useLang();
  const { data: settings } = useSettings();
  const onlineUrl = settings?.online_islemler_url || "https://oim.entanet.com.tr";

  return (
    <footer className="bg-[#101436] text-zinc-400" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-3">
            <Link to="/" className="flex items-center gap-2" data-testid="footer-logo">
              <img src="/entalogo.png" alt="EntaNET" className="h-9 w-auto" />
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed">{t("footer.tagline")}</p>
            <div className="mt-6 flex gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors hover:border-[#ff9800] hover:text-[#ff9800]"
                >
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
            <p className="mt-6 text-xs leading-relaxed text-zinc-600">
              {settings?.company_fullname || "ENTA-NET TELEKOMÜNİKASYON VE BİLGİ TEKNOLOJİLERİ TİCARET LİMİTED ŞİRKETİ"}
            </p>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-white">{t("footer.quickLinks")}</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/tarifeler" className="transition-colors hover:text-[#ff9800]">{t("nav.packages")}</Link></li>
              <li><Link to="/kampanyalar" className="transition-colors hover:text-[#ff9800]">{t("nav.campaigns")}</Link></li>
              <li><Link to="/kurumsal" className="transition-colors hover:text-[#ff9800]">{t("nav.corporate")}</Link></li>
              <li><Link to="/blog" className="transition-colors hover:text-[#ff9800]">{t("nav.blog")}</Link></li>
              <li><Link to="/sss" className="transition-colors hover:text-[#ff9800]">{t("nav.faq")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-white">{t("footer.services")}</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/hizmetler" className="transition-colors hover:text-[#ff9800]">{t("nav.services")}</Link></li>
              <li><Link to="/iletisim" className="transition-colors hover:text-[#ff9800]">{t("nav.contact")}</Link></li>
              <li>
                <a
                  href={onlineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-[#ff9800]"
                  data-testid="footer-online-islemler"
                >
                  {t("nav.online")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-white">Bilgilendirme</h3>
            <ul className="mt-4 space-y-2.5 text-sm">
              {legalLinks.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="transition-colors hover:text-[#ff9800]">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <h3 className="text-sm font-bold uppercase tracking-[0.2em] text-white">{t("footer.contact")}</h3>
            <ul className="mt-4 space-y-3.5 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#ff9800]" />
                <span data-testid="footer-address">{settings?.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 shrink-0 text-[#ff9800]" />
                <a href={`tel:${(settings?.phone || "").replace(/\s/g, "")}`} className="transition-colors hover:text-[#ff9800]" data-testid="footer-phone">
                  {settings?.phone}
                </a>
              </li>
              {(settings?.emails || []).map((email) => (
                <li key={email} className="flex items-center gap-3">
                  <Mail className="h-4 w-4 shrink-0 text-[#ff9800]" />
                  <a href={`mailto:${email}`} className="transition-colors hover:text-[#ff9800]">{email}</a>
                </li>
              ))}
            </ul>
            <a
              href={`tel:${(settings?.phone || "").replace(/\s/g, "")}`}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-call px-6 py-2.5 text-sm font-bold text-white transition-opacity hover:opacity-90"
            >
              <Phone className="h-4 w-4" />
              {settings?.phone}
            </a>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 text-xs text-zinc-600 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} EntaNET. {t("footer.rights")}</p>
          <Link to="/admin/login" className="transition-colors hover:text-zinc-400" data-testid="footer-admin-link">Admin</Link>
        </div>
      </div>
    </footer>
  );
}
